/**
 * Discount Utility Functions
 * Handles calculation and validation of discounts based on the V2 refactor
 */

import { Discount } from '@/lib/Apis/models/discount';
import { 
  DiscountTypeEnum, 
  DiscountValueTypeEnum, 
  DiscountStateEnum 
} from '@/lib/Apis/models/discount';

export { DiscountTypeEnum, DiscountValueTypeEnum, DiscountStateEnum };

export interface DiscountsInsightsDto {
  totalDiscounts: number;
  totalActive: number;
  totalInactive: number;
  totalExpired: number;
  totalScheduled: number;
  totalItemDiscounts: number;
  totalCouponCodes: number;
}

/**
 * Calculate discount amount for a given price
 * @param price - Original price
 * @param discount - Discount object
 * @returns Calculated discount amount
 */
export function calculateDiscountAmount(price: number, discount: Discount): number {
  // Skip state validation - trust that backend already validated the discount
  // Just check if we have the required fields to calculate
  if (!discount || !discount.value) {
    return 0;
  }

  let amount = 0;
  const valueType = String(discount.valueType).toUpperCase();

  if (valueType === 'PERCENTAGE') {
    amount = (price * discount.value) / 100;
    // Apply cap if specified (mainly for coupon codes)
    if (discount.cappedAt && amount > discount.cappedAt) {
      amount = discount.cappedAt;
    }
  } else if (valueType === 'FIXED_AMOUNT' || valueType === 'FIXEDAMOUNT') {
    amount = discount.value;
  }

  const finalAmount = Math.min(amount, price);
  return Math.round(finalAmount * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculate final price after discount
 * @param price - Original price
 * @param discount - Discount object
 * @returns Final discounted price
 */
export function calculateFinalPrice(price: number, discount: Discount): number {
  const discountAmount = calculateDiscountAmount(price, discount);
  return Math.max(0, price - discountAmount);
}

/**
 * Check if a discount is currently valid (active and not expired/scheduled)
 * @param discount - Discount object
 * @returns True if discount is valid and can be applied
 */
export function isDiscountValid(discount: Discount): boolean {
  if (!discount.isActive) {
    return false;
  }

  const now = new Date();

  // Check if scheduled
  if (discount.startsAt && new Date(discount.startsAt) > now) {
    return false; // Not yet started
  }

  // Check if expired
  if (discount.expiresAt && new Date(discount.expiresAt) < now) {
    return false; // Already expired
  }

  // Check state
  const activeStates = [
    DiscountStateEnum.ACTIVE,
    'ACTIVE',
  ];
  
  if (!activeStates.includes(discount.state as any)) {
    return false;
  }

  return true;
}

/**
 * Check if a product is eligible for an item discount
 * @param productId - Product ID
 * @param variantId - Variant ID (optional)
 * @param discount - Discount object
 * @returns True if product/variant is eligible
 */
export function isProductEligibleForDiscount(
  productId: string,
  variantId: string | undefined,
  discount: Discount
): boolean {
  // Check variant eligibility first (most specific)
  if (variantId && discount.variantsEligible?.includes(variantId)) {
    return true;
  }

  // Check product eligibility
  if (discount.productsEligible?.includes(productId)) {
    return true;
  }

  return false;
}

/**
 * Format discount display text
 * @param discount - Discount object
 * @returns Formatted string like "20% OFF" or "$10 OFF"
 */
export function formatDiscountDisplay(discount: Discount): string {
  const valueType = String(discount.valueType).toUpperCase();
  
  if (valueType === 'PERCENTAGE') {
    return `${discount.value}% OFF`;
  } else {
    return `$${discount.value.toFixed(2)} OFF`;
  }
}

/**
 * Calculate discount amount for a list of cart items, only applying to eligible products/variants.
 * If the discount has no eligibility restrictions it applies to the full cart subtotal.
 * @param items - Cart items (CartItemPopulated[])
 * @param discount - Discount object
 * @returns Total discount amount
 */
export function calculateDiscountForCartItems(
  items: Array<{
    productVariantId?: string;
    quantity: number;
    productVariantData: { id?: string; _id?: string; productId: string; finalPrice: number };
  }>,
  discount: Discount
): number {
  const hasRestrictions =
    (discount.productsEligible?.length ?? 0) > 0 ||
    (discount.variantsEligible?.length ?? 0) > 0;

  const eligibleSubtotal = items.reduce((sum, item) => {
    if (hasRestrictions) {
      const variantId =
        item.productVariantId ||
        item.productVariantData?.id ||
        item.productVariantData?._id;
      const productId = item.productVariantData?.productId;
      if (!isProductEligibleForDiscount(productId, variantId, discount)) {
        return sum;
      }
    }
    return sum + item.productVariantData.finalPrice * item.quantity;
  }, 0);

  return calculateDiscountAmount(eligibleSubtotal, discount);
}

/**
 * Get discount state label
 * @param state - Discount state
 * @returns Human-readable state label
 */
export function getDiscountStateLabel(state: DiscountStateEnum | string): string {
  const stateStr = String(state).toUpperCase();
  const stateMap: Record<string, string> = {
    'ACTIVE': 'Active',
    'INACTIVE': 'Inactive',
    'EXPIRED': 'Expired',
    'SCHEDULED': 'Scheduled',
  };
  return stateMap[stateStr] || 'Unknown';
}
